import React, { FC, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { Loader, Box, Text, StatusIndicator } from '@wix/design-system';
import { useIntersectionObserver } from 'usehooks-ts';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useExploreContext, ACTIONS } from '../../Contexts/ExploreContext';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import {
  ENABLE_BRIEF_PROMO_CARD,
  ENABLE_YELL_REDIRECT,
} from '@utils/constants';
import SortMenu from '../SortMenu';
import Provider from '../Provider';
import BriefPromoCardDesktop from '../BriefPromoCard/BriefPromoCardDesktop';
import BriefPromoCardMobile from '../BriefPromoCard/BriefPromoCardMobile';
import { useBi } from '../../Hooks/useBi';
import s from './Providers.module.scss';

type Props = {
  infiniteScroll?: boolean;
};

const Providers: FC<Props> = ({ infiniteScroll }) => {
  const {
    dispatch,
    state: {
      providers,
      SSRFlow,
      appStatus,
      selectedServices,
      selectedLanguages,
      selectedPricing,
      selectedSortingMethod,
      selectedLocation,
      currencyConversionRate,
      numberOfResults,
      currentPage,
      numberOfPages,
      sessionId,
      origin,
      entry,
      isMobileUserAgent,
    },
  }: { dispatch: any; state: any } = useExploreContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const { experiments } = useExperiments({ readOnly: true });
  const isBriefPromoCardEnabled =
    experiments.enabled(ENABLE_BRIEF_PROMO_CARD) &&
    !experiments.enabled(ENABLE_YELL_REDIRECT);
  const { t } = useTranslation();
  const logger = useBi();
  const { isIntersecting, ref } = useIntersectionObserver({});

  const hasNextPage = useCallback(
    () => currentPage < numberOfPages,
    [currentPage, numberOfPages],
  );

  useEffect(() => {
    if (
      infiniteScroll &&
      isIntersecting &&
      hasNextPage() &&
      appStatus === 'initialized'
    ) {
      dispatch({
        type: ACTIONS.FETCH_MORE_PROVIDERS,
        page: currentPage + 1,
        selectedServices,
        selectedLanguages,
        selectedPricing,
        selectedSortingMethod,
        selectedLocation,
        currencyConversionRate,
        reservedSlots: isBriefPromoCardEnabled ? 1 : 0,
      });
    }
  }, [
    appStatus,
    currentPage,
    dispatch,
    isIntersecting,
    hasNextPage,
    selectedLanguages,
    selectedLocation,
    selectedPricing,
    selectedServices,
    selectedSortingMethod,
  ]);

  useEffect(() => {
    if (isIntersecting) {
      logger.report(
        exploreResultsPageGenericClick({
          clickType: 'scroll partners results',
          sessionId,
          origin,
          entry,
          exploreVersion: 'V2',
        }),
      );
    }
  }, [isIntersecting, sessionId, origin, entry]);

  return (
    <>
      {appStatus === 'loading' ? (
        <Box marginTop="10%" align="center" verticalAlign="middle">
          <Loader size="large" />
        </Box>
      ) : (
        <>
          <Box
            dataHook="explore-providers-professionals-found"
            direction="horizontal"
            align="space-between"
            marginBottom="30px"
            marginTop="36px"
          >
            <Text
              tagName="h2"
              className={s.providerCount}
              dataHook="professionals-found"
            >
              {t(`explore-professional-found.icu`, {
                number: numberOfResults,
              })}
            </Text>
            <SortMenu />
          </Box>
          <ul className={s.partners}>
            {isBriefPromoCardEnabled && !isMobileView && currentPage === 1 && (
              <li key="brief-promo-card">
                <BriefPromoCardDesktop />
              </li>
            )}
            {_.map(providers, (provider, index: number) => (
              <React.Fragment key={provider.id}>
                <li>
                  <Provider
                    {...provider}
                    autoFocus={index === 0}
                    forceRender={SSRFlow}
                  />
                </li>
                {isBriefPromoCardEnabled &&
                  isMobileView &&
                  currentPage === 1 &&
                  index === 1 && (
                    <li>
                      <BriefPromoCardMobile />
                    </li>
                  )}
              </React.Fragment>
            ))}
          </ul>
          {infiniteScroll && hasNextPage() && (
            <div ref={ref} data-hook="infiniteScroll-container">
              <Box marginTop="26px" align="center" verticalAlign="middle">
                <StatusIndicator status="loading" />
              </Box>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Providers;
